import {
  Container,
  Text,
  Title,
  Grid,
  Box,
  ComboboxData,
  ComboboxItem,
} from "@mantine/core"
import { useForm, UseFormReturnType } from "@mantine/form"
import _ from "lodash"
import { zodResolver } from "mantine-form-zod-resolver"
import { useState, FC, useCallback, useEffect, useMemo } from "react"

import {
  alertCreationSchema,
  AlertsRequests,
  AlertsResponses,
} from "@costory/types/endpoints/alerts"
import { Filters } from "@costory/types/filters"
import {
  Alert,
  AlertPeriod,
  AlertType,
  Currency,
  VirtualDimension,
} from "@costory/types/prisma-client"

import { ALL_VALUES } from "@costory/shared/const"
import { computePresetDates } from "@costory/shared/utils/filters"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { AlertConfigurationForm } from "@costory/front/pages/Alerts/AlertConfigurationForm"
import AlertPreview from "@costory/front/pages/Alerts/AlertPreview"
import { NoVirtualDimensionsPage } from "@costory/front/pages/Alerts/NoVirtualDimensionsPage"
import { useAlertFormDataQuery } from "@costory/front/queries/alerts"
import { PropsWithData } from "@costory/front/utils/propsWithData"

export type AlertFormData = AlertsRequests.AlertCreation
type ComboboxItemWithProps<T> = ComboboxItem & T
interface AlertFormProps extends PropsWithData<AlertsResponses.AlertFormData> {
  initialValues: Partial<Alert>
  onSave: (values: AlertFormData) => Promise<void>
  mode?: "create" | "edit"
}

export const AlertForm: FC<Omit<AlertFormProps, "data">> = (props) => {
  const query = useAlertFormDataQuery()
  return (
    <QueryWrapper query={query}>
      {({ data }) => <_AlertForm data={data} {...props} />}
    </QueryWrapper>
  )
}

const _AlertForm: FC<AlertFormProps> = ({
  data,
  initialValues: { businessMetricId, ...initialValues },
  onSave,
  mode = "create",
}) => {
  const {
    virtualDimensions,
    businessMetrics,
    availableOwners,
    existingAlerts,
    existingSlackChannels,
    slackUsers,
    budgets,
  } = data

  const isEditMode = mode === "edit"

  const [selectedVirtualDimension, setSelectedVirtualDimension] =
    useState<VirtualDimension>()
  const [searchTerm, setSearchTerm] = useState<string>("")
  const existingAlertsHashes = useMemo(
    () =>
      existingAlerts.map(
        (alert) => alert.virtualDimensionId + alert.virtualDimensionValue,
      ),
    [existingAlerts],
  )

  const doesAlertAlreadyExist = useCallback(
    (virtualDimensionId: string, virtualDimensionValue: string) => {
      return existingAlertsHashes.includes(
        virtualDimensionId + virtualDimensionValue,
      )
    },
    [existingAlertsHashes],
  )

  const getVirtualDimension = useCallback(
    (id: string) =>
      virtualDimensions.find((virtualDimension) => virtualDimension.id === id),
    [virtualDimensions],
  )

  const [activeTab, setActiveTab] = useState<number>(0)

  const form = useForm<AlertFormData>({
    // @ts-expect-error that's because of the partial initial values
    initialValues: {
      currency: Currency.USD,
      period: AlertPeriod.MONTHLY,
      businessMetricId: businessMetricId ?? "cost",
      slackChannelIds: [],
      useForecastedValues: false,
      type: (AlertType?.THRESHOLD ?? "THRESHOLD") as AlertType,
      ...initialValues,
    },
    validate: zodResolver(alertCreationSchema),
  })
  useEffect(() => {
    setSelectedVirtualDimension(
      getVirtualDimension(form.values.virtualDimensionId),
    )
  }, [form.values.virtualDimensionId, getVirtualDimension])

  form.watch("virtualDimensionId", () => {
    // @ts-expect-error mantine doesn't allow to set null value
    form.setFieldValue("virtualDimensionValue", null)
  })

  const virtualDimensionsSelectItems: ComboboxItem[] = useMemo(
    () =>
      virtualDimensions.map((virtualDimension) => ({
        value: virtualDimension.id,
        label: virtualDimension.name,
      })),
    [virtualDimensions],
  )

  const businessMetricsSelectItems: ComboboxData = useMemo(
    () =>
      businessMetrics.map((businessMetric) => ({
        value: businessMetric.id,
        label: businessMetric.name,
      })),
    [businessMetrics],
  )

  const currencySelectItems: ComboboxData = useMemo(
    () =>
      Object.values(Currency).map((value) => ({
        value,
        label: value,
      })),
    [],
  )

  const periodSelectItems: ComboboxData = useMemo(
    () =>
      Object.values(AlertPeriod).map((value) => ({
        value,
        label: _.upperFirst(value.toLowerCase()),
      })),
    [],
  )

  const ownerSelectItems: ComboboxItem[] = useMemo(
    () =>
      availableOwners.map((owner) => ({
        value: owner.id,
        label: _.startCase(owner.firstName + " " + owner.lastName),
      })),
    [availableOwners],
  )

  const virtualDimensionValueSelectItems = useMemo(() => {
    if (!selectedVirtualDimension) {
      return []
    }

    return [ALL_VALUES, ..._.uniq(selectedVirtualDimension.values)].map(
      (virtualDimensionValue): ComboboxItemWithProps<{ exists: boolean }> => ({
        value: virtualDimensionValue,
        label:
          virtualDimensionValue === ALL_VALUES
            ? "All values"
            : virtualDimensionValue,
        exists: (() => {
          const exists = doesAlertAlreadyExist(
            selectedVirtualDimension.id,
            virtualDimensionValue,
          )
          return exists
        })(),
      }),
    )
  }, [selectedVirtualDimension, doesAlertAlreadyExist])
  const filteredSlackItems = () => {
    if (!searchTerm) {
      return _.slice(existingSlackChannels, 0, 50)
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase()

    return [...existingSlackChannels, ...slackUsers].filter((item) =>
      _.includes(item.label.toLowerCase(), lowerCaseSearchTerm),
    )
  }
  // Compute filters only if all required fields are filled
  const filters: Filters | undefined =
    selectedVirtualDimension &&
    form.values.virtualDimensionValue &&
    form.values.businessMetricId &&
    form.values.period &&
    form.values.currency
      ? {
          limit: 3,
          aggBy: "Week",
          metricId: form.values.businessMetricId,
          groupBy: selectedVirtualDimension.bqName,
          currency: form.values.currency,
          ...computePresetDates(
            form.values.period === "MONTHLY"
              ? "LAST_12_MONTHS"
              : form.values.period === "WEEKLY"
                ? "LAST_3_MONTHS"
                : "LAST_WEEK",
          ),
          whereClause: {
            combinator: "and",
            rules: [
              {
                field: selectedVirtualDimension.bqName,
                operator: "in",
                valueSource: "value",
                value:
                  form.values.virtualDimensionValue === ALL_VALUES
                    ? [...selectedVirtualDimension.values]
                    : [form.values.virtualDimensionValue],
              },
            ],
          },
        }
      : undefined

  if (virtualDimensions.length === 0) {
    return <NoVirtualDimensionsPage />
  }

  const selectedVirtualDimensionLabel = virtualDimensionsSelectItems?.find(
    (_) => form.values.virtualDimensionId == _.value,
  )?.label

  const selectedOwner = ownerSelectItems.find(
    (_) => _.value == form.values.ownerId,
  )

  return (
    // @ts-expect-error that's because of the partial initial values
    <form onSubmit={form.onSubmit(onSave)}>
      <Container size="xl" py="xl" px="md">
        <Box mb="xl">
          <Title order={1} mb="xs">
            Create Alert
          </Title>
          <Text c="dimmed" size="md">
            Set up notifications for cost anomalies and budget thresholds
          </Text>
        </Box>

        <Grid gutter="xl">
          <AlertConfigurationForm
            // @ts-expect-error that's because of the partial initial values
            form={form as UseFormReturnType<AlertFormData>}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            virtualDimensionsSelectItems={virtualDimensionsSelectItems}
            virtualDimensionValueSelectItems={virtualDimensionValueSelectItems}
            periodSelectItems={periodSelectItems}
            currencySelectItems={currencySelectItems}
            businessMetricsSelectItems={businessMetricsSelectItems}
            ownerSelectItems={ownerSelectItems}
            existingSlackChannels={existingSlackChannels}
            slackUsers={slackUsers}
            setSearchTerm={setSearchTerm}
            filteredSlackItems={filteredSlackItems}
            isEditMode={isEditMode}
            filters={filters}
            budgets={budgets}
          />
          <AlertPreview
            // @ts-expect-error that's because of the partial initial values
            form={form as UseFormReturnType<AlertFormData>}
            filters={filters}
            selectedOwner={selectedOwner}
            selectedVirtualDimensionLabel={selectedVirtualDimensionLabel}
            existingSlackChannels={existingSlackChannels}
          />
        </Grid>
      </Container>
    </form>
  )
}
